import moment from 'moment';
import i18next from 'i18next';
import { Button, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';
import { StickyTable } from 'datastake-daf/dist/components';
import Island from './island';
import { mapMeasures, measuresColumns } from './helpers';

const columns = (t, linkToEvent, actions = {}) => [
  ...measuresColumns(t, linkToEvent),
  {
    dataIndex: 'actions',
    title: '',
    render: (val, all) => all.isEvent ? (
      <Popconfirm
        okText={t('Yes, remove')}
        cancelText={t('No, keep')}
        title={
          <label
            style={{ maxWidth: 400, fontWeight: 500, fontSize: 16, marginBottom: 0 }}
          >
            {t('Do you want to remove action')}
            {' '}
            <span style={{ color: 'var(--color-primary-70)' }}>
              {all.id}
              {' '}
            </span>
            {t('from')}
            {' '}
            <span style={{ color: 'var(--color-primary-70)' }}>
              {t('Confirmed Events')}?
            </span>
          </label>
        }
        onConfirm={() => {
          if (typeof actions.disconnect === 'function') {
            actions.disconnect(all.measureId, all.id);
          }
        }}
        okButtonProps={{ size: 'middle' }}
        cancelButtonProps={{ size: 'middle' }}
      >
        <Button>
          {t('Disconnect')}
        </Button>
      </Popconfirm>
    ) : (all.children && all.children.length) ? (
      <Popconfirm
        okText={t('Yes, measure is completed')}
        cancelText={t('No, keep measure in progress')}
        title={
          <label
            style={{ maxWidth: 400, fontWeight: 500, fontSize: 16, marginBottom: 0 }}
          >
            {t('Confirm that the following measure is completed')}
            {': '}
            <span style={{ color: 'var(--color-primary-70)' }}>
              {all.title}
              {' '}
            </span>
          </label>
        }
        onConfirm={() => {
          if (typeof actions.complete === 'function') {
            actions.complete(all.id);
          }
        }}
        okButtonProps={{ size: 'middle' }}
        cancelButtonProps={{ size: 'middle' }}
      >
        <Button
          type="primary"
        >
          {t('Complete')}
        </Button>
      </Popconfirm>
    ) : (
      <Button
        type="primary"
        disabled
      >
        {t('Complete')}
      </Button>
    ),
  },
];

export default function RiskMitigation({
  measures = [],
  measuresValue = [],
  events = [],
  disconnectEvent = () => { },
  completeMeasure = () => { },
  linkToEvent = () => '',
  inputLabel,
}) {
  const { t } = useTranslation();
  const [toggledKeys, setToggledKeys] = useState([]);

  const data = useMemo(() => mapMeasures({
    measures,
    measuresValue,
    status: 'proposed',
    events,
  }), [measures, measuresValue, events]);

  const lastUpdate = useMemo(() => {
    let all = [];

    (data || []).forEach((d) => {
      all.push(d.createdAt);
    });

    all = all.sort((a, b) => moment(b).diff(moment(a)));

    if (all[0]) {
      return all[0];
    }

    return null;
  }, [data]);

  const legend = useMemo(() => {
    const proposed = data.filter((d) => !d.children ? true : d.children.length === 0).length;
    const inProgress = data.filter((d) => !d.children ? false : d.children.length).length;
    return {
      proposed: {
        value: proposed,
        label: t('Proposed'),
        color: '#D3ADF7',
      },
      inProgress: {
        value: inProgress,
        label: t('In Progress'),
        color: '#ADC6FF',
      },
    };
  }, [data, t]);

  const renderedColumns = columns(t, linkToEvent, {
    disconnect: (measureId, eventId) => {
      disconnectEvent(measureId, eventId);
    },
    complete: (measureId) => {
      completeMeasure(measureId);
    },
  });

  const onToggleChanged = (val) => {
    setToggledKeys(val);
  };

  return (
    <Island
      headerText={t('Risk Mitigation')}
      legend={legend}
      lastUpdate={lastUpdate}
      t={t}
      mT
      desc={inputLabel[i18next.language] ? inputLabel[i18next.language] : null}
    >
      <div style={{ overflowX: 'auto' }}>
        <StickyTable
          rowKey="id"
          dataSource={data}
          columns={renderedColumns}
          size="small"
          expandable={{
            expandedRowKeys: toggledKeys,
            onExpandedRowsChange: (rows) => onToggleChanged(rows),
            rowExpandable: false,
          }}
          rowClassName={(rec) => toggledKeys.includes(rec.key) ? 'expanded' : ''}
        />
      </div>
    </Island>
  );
}
